import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { navLinks, pagesDropdown, veagroupLogo } from '../data/home/home';

const MOBILE_MENU = 'main';
const MOBILE_PAGES = 'pages';

const Header: React.FC = () => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobilePanel, setMobilePanel] = useState(MOBILE_MENU);
  const location = useLocation();
  const dropdownTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setMobilePanel(MOBILE_MENU);
    }
  }, [mobileMenuOpen]);

  // Helper for closing menu and resetting panel
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setTimeout(() => setMobilePanel(MOBILE_MENU), 300);
  };

  // Dropdown hover delay logic
  const handleDropdownEnter = () => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setIsDropdownOpen(true);
  };
  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setIsDropdownOpen(false), 150);
  };

  return (
    <header
      className={`header w-full top-0 left-0 z-50 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
        isScrolled 
          ? 'fixed shadow-md bg-white/ dark:bg-gray-900/90 backdrop-blur-sm py-2' 
          : 'absolute bg-transparent py-6'
      }`}
    >
      <nav className="navbar container flex items-center justify-between">
        {/* logo */}
        <div className="order-0 flex items-center">
          <Link to="/">
            <img src={veagroupLogo} alt="VEA Group Logo" className="h-6 w-auto" />
          </Link>
        </div>
        {/* Desktop Navigation Menu */}
        <ul className="hidden lg:flex items-center space-x-8 mx-auto">
          {navLinks.slice(0, 4).map((link) => (
            <li key={link.to} className="relative">
              <Link
                to={link.to}
                className={`nav-link px-1 pb-1 font-primary text-base transition-colors duration-200 ${
                  location.pathname === link.to
                    ? 'text-theme_dark dark:text-white font-semibold'
                    : 'text-theme_dark dark:text-gray-300 hover:text-primary'
                }`}
              >
                {link.label}
              </Link>
              {location.pathname === link.to && (
                <span className="absolute left-0 right-0 -bottom-1 h-0.5 rounded-full bg-gradient-to-r from-primary to-secondary"></span>
              )}
            </li>
          ))}
          {/* Pages Dropdown */}
          <li
            className="relative group"
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleDropdownLeave}
          >
            <button
              className="nav-link flex items-center px-1 pb-1 font-primary text-base text-theme_dark dark:text-gray-300 hover:text-primary transition-colors duration-200 focus:outline-none"
            >
              Internal
              <svg
                className="ml-1 h-4 w-4 text-primary"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isDropdownOpen && (
              <ul
                className="absolute right-0 mt-3 w-56 rounded-xl border border-border dark:border-gray-700 bg-white dark:bg-gray-800 py-2 shadow-lg z-50 animate-fade-in"
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
              >
                {pagesDropdown.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="block px-5 py-2 text-theme_dark dark:text-gray-300 font-primary text-base hover:bg-theme_light dark:hover:bg-gray-700 hover:text-primary rounded-lg transition-colors duration-150"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
          {/* Contact Link */}
          <li key={navLinks[4].to} className="relative">
            <Link
              to={navLinks[4].to}
              className={`nav-link px-1 pb-1 font-primary text-base transition-colors duration-200 ${
                location.pathname === navLinks[4].to
                  ? 'text-theme_dark dark:text-white font-semibold'
                  : 'text-theme_dark dark:text-gray-300 hover:text-primary'
              }`}
            >
              {navLinks[4].label}
            </Link>
            {location.pathname === navLinks[4].to && (
              <span className="absolute left-0 right-0 -bottom-1 h-0.5 rounded-full bg-gradient-to-r from-primary to-secondary"></span>
            )}
          </li>
          {/* Desktop Dark Mode Toggle */}
          <li className="flex items-center">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
          </li>
        </ul>
        {/* Mobile Menu Controls */}
        <div className="lg:hidden flex items-center space-x-2">
          {/* Mobile Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? (
              <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>
          {/* Hamburger Icon */}
          <button
            className="flex flex-col items-center justify-center w-10 h-10 focus:outline-none"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <span className="block w-7 h-0.5 bg-theme_dark dark:bg-white mb-1.5 rounded transition-all duration-300" />
            <span className="block w-7 h-0.5 bg-theme_dark dark:bg-white mb-1.5 rounded transition-all duration-300" />
            <span className="block w-7 h-0.5 bg-theme_dark dark:bg-white rounded transition-all duration-300" />
          </button>
        </div>
        {/* Mobile Menu Overlay and Panel */}
        {/* Dimmed Overlay */}
        <div
          className={`fixed inset-0 z-[998] bg-black bg-opacity-40 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          onClick={closeMobileMenu}
        />
        {/* Slide-in Panel */}
        <div
          className={`fixed top-0 right-0 z-[999] h-full w-96 max-w-full bg-white dark:bg-gray-900 bg-opacity-95 rounded-l-3xl shadow-2xl flex flex-col transition-transform duration-300 ${
            mobileMenuOpen && mobilePanel === MOBILE_MENU ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ minWidth: '320px' }}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-6 py-1">
              <Link to="/" className="flex items-center">
                <img src={veagroupLogo} alt="VEA Group Logo" className="h-5 w-auto" />
              </Link>
              <button
                className="w-10 h-10 flex items-center justify-center text-theme_dark dark:text-white focus:outline-none"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-evenly items-start px-8">
              {navLinks.slice(0, 4).map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={closeMobileMenu}
                  className="text-theme_dark dark:text-white font-primary text-base font-medium hover:text-primary transition-colors duration-200 w-full text-left"
                >
                  {link.label}
                </Link>
              ))}
              <button
                className="flex items-center text-theme_dark dark:text-white font-primary text-base font-medium hover:text-primary transition-colors duration-200 w-full text-left"
                onClick={() => setMobilePanel(MOBILE_PAGES)}
              >
                Internal
                <svg
                  className="ml-1 h-4 w-4 text-primary"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <Link
                key={navLinks[4].to}
                to={navLinks[4].to}
                onClick={closeMobileMenu}
                className="text-theme_dark dark:text-white font-primary text-base font-medium hover:text-primary transition-colors duration-200 w-full text-left"
              >
                {navLinks[4].label}
              </Link>
            </nav>
          </div>
        </div>
        {/* Slide-in Dropdown Panel */}
        <div
          className={`fixed top-0 right-0 z-[1000] h-full w-96 max-w-full bg-white dark:bg-gray-900 bg-opacity-95 rounded-l-3xl shadow-2xl flex flex-col transition-transform duration-300 ${
            mobileMenuOpen && mobilePanel === MOBILE_PAGES ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ minWidth: '320px' }}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-6 py-1">
              <button
                className="w-10 h-10 flex items-center justify-center text-theme_dark dark:text-white focus:outline-none"
                onClick={() => setMobilePanel(MOBILE_MENU)}
                aria-label="Back"
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-theme_dark dark:text-white font-primary text-lg font-semibold">Internal</span>
              <button
                className="w-10 h-10 flex items-center justify-center text-theme_dark dark:text-white focus:outline-none"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-evenly items-start px-8">
              {pagesDropdown.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMobileMenu}
                  className="text-theme_dark dark:text-white font-primary text-base font-medium hover:text-primary transition-colors duration-200 w-full text-left"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
